import type { ReactNode } from 'react';
import { Fragment, createContext, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { StyledContainer, StyledRowContainer, StyledCountdownAndTextContainer, StyledLabel } from './Snackbar.view';
import type { HideSnackbar, ShowSnackbarEvent, SnackbarOptions } from './types';
import { DEFAULT_DURATION, HIDE_SNACKBAR_EVENT_TYPE, SHOW_SNACKBAR_EVENT_TYPE } from './constants';

// Legacy: используется только в deprecated useSnackbar. Удалить вместе с ним.
export const SnackbarContext = createContext<{
    show: (label: string, options: SnackbarOptions) => void;
    hide: HideSnackbar;
}>({
    show: () => {},
    hide: () => {},
});

/**
 * State текущего показа для слотов (left/right). Отдельный контекст, чтобы изменения
 * при show() не ререндерили потребителей стабильных show/hide из SnackbarContext.
 */
export const SnackbarStateContext = createContext<{ duration: number | null }>({
    duration: null,
});

type SnackbarProviderProps = {
    children: ReactNode;
    className?: string;
};

export const SnackbarProvider = ({ children, className }: SnackbarProviderProps) => {
    const dismissTimer = useRef<number | undefined>(undefined);

    const [isVisible, setIsVisible] = useState(false);
    const [options, setOptions] = useState<null | (SnackbarOptions & { label: string; showId: number })>(null);

    const { label, type = 'default', left, right, testId, duration = DEFAULT_DURATION, showId = 0 } = options || {};

    const hide = useCallback(() => {
        clearTimeout(dismissTimer.current);
        setIsVisible(false);
    }, []);

    const show = useCallback(
        (label: string, params: SnackbarOptions = {}) => {
            clearTimeout(dismissTimer.current);

            setOptions(prev => ({
                label,
                ...params,
                showId: (prev?.showId ?? 0) + 1,
            }));

            setIsVisible(true);

            const { duration = DEFAULT_DURATION } = params;

            dismissTimer.current = window.setTimeout(hide, duration);
        },
        [hide],
    );

    useEffect(
        function registerEventListeners() {
            const handler = (event: ShowSnackbarEvent) => {
                const {
                    detail: { label, options },
                } = event;

                show(label, options);
            };

            window.addEventListener(SHOW_SNACKBAR_EVENT_TYPE, handler as (event: Event) => void);
            window.addEventListener(HIDE_SNACKBAR_EVENT_TYPE, hide);

            return () => {
                window.removeEventListener(SHOW_SNACKBAR_EVENT_TYPE, handler as (event: Event) => void);
                window.removeEventListener(HIDE_SNACKBAR_EVENT_TYPE, hide);
            };
        },
        [hide, show],
    );

    const value = useMemo(
        () => ({
            show,
            hide,
        }),
        [hide, show],
    );

    const snackbarState = useMemo(
        () => ({
            duration,
        }),
        [duration],
    );

    return (
        <SnackbarContext.Provider value={value}>
            {children}

            <SnackbarStateContext.Provider value={snackbarState}>
                <StyledContainer
                    type={type}
                    $visible={isVisible}
                    className={className}
                    data-cy={testId}
                >
                    <StyledRowContainer>
                        <StyledCountdownAndTextContainer>
                            {left && <Fragment key={`left-${showId}`}>{left}</Fragment>}

                            <StyledLabel
                                type={type}
                                variant="body2"
                                $hasSideSlot={Boolean(left) || Boolean(right)}
                            >
                                {label}
                            </StyledLabel>
                        </StyledCountdownAndTextContainer>

                        {right && <Fragment key={`right-${showId}`}>{right}</Fragment>}
                    </StyledRowContainer>
                </StyledContainer>
            </SnackbarStateContext.Provider>
        </SnackbarContext.Provider>
    );
};
